import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountHooksService } from '../account-hooks.service';
import { AccountHook } from '../account-hooks.types';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import clone from 'lodash-es/clone';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';

@Component({
  selector: 'account-hooks-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountHooksDetailsComponent implements OnInit, OnDestroy {
  accountHooks: AccountHook[];
  accountHook: AccountHook;
  replyFormActive: boolean;
  accountHookForm: UntypedFormGroup;

  // Private
  private _overlayRef: OverlayRef;
  private _unsubscribeAll: Subject<void>;

  /**
   * Constructor
   *
   * @param {ActivatedRoute} _activatedRoute
   * @param {ElementRef} _elementRef
   * @param {Overlay} _overlay
   * @param {Router} _router
   * @param {ViewContainerRef} _viewContainerRef
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _elementRef: ElementRef,
    private _accountHooksService: AccountHooksService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _overlay: Overlay,
    private _formBuilder: UntypedFormBuilder,
    private _router: Router,
    private _viewContainerRef: ViewContainerRef,
    private store: Store,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // Set the default
    this.replyFormActive = false;
  }

  get isActionsDisabled(): boolean {
    const user = this.store.selectSnapshot(UserState.getUser);
    return !user?.email?.includes('@termsheet.com');
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.accountHookForm = this._formBuilder.group({
      id: [''],
      hook_code: [''],
      processing_mode: [''],
      active: [''],
      rank: [0, [Validators.required, Validators.pattern('^[0-9]*$')]],
    });

    // Folders
    this._accountHooksService.accountHooks$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((accountHooks: AccountHook[]) => {
        this.accountHooks = accountHooks;
      });

    // Mail
    this._accountHooksService.accountHook$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((accountHook: AccountHook) => {
        this.accountHook = accountHook;
        this.accountHookForm.patchValue(accountHook, { emitEvent: false });
        this._changeDetectorRef.markForCheck();
      });

    this._accountHooksService.selectedAccountHookChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {});
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  discard() {}

  save() {
    let accountHook = clone(this.accountHookForm.value);

    this._accountHooksService.saveAccountHook(accountHook).subscribe((af) => {});
  }

  delete() {
    if (confirm('Are you sure?')) {
      this._accountHooksService.deleteAccountHook(this.accountHook.id).subscribe((af) => {
        this._router.navigate(['/settings/hooks']);
      });
    }
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
